/**
 * Shared therapy-related constants
 */

// Areas of Focus categories with their items
export const AREAS_OF_FOCUS = [
  {
    category: "Anxiety & Stress",
    items: [
      "Generalized Anxiety",
      "Social Anxiety", 
      "Panic Attacks",
      "Phobias",
      "OCD",
      "Health Anxiety",
      "Performance Anxiety",
      "Stress Management",
      "Burnout",
      "Overwhelm"
    ]
  },
  {
    category: "Depression & Mood",
    items: [
      "Major Depression",
      "Persistent Depressive Disorder",
      "Seasonal Affective Disorder",
      "Postpartum Depression",
      "Bipolar Disorder",
      "Mood Swings",
      "Emotional Regulation",
      "Suicidal Ideation",
      "Hopelessness",
      "Grief & Loss"
    ]
  },
  {
    category: "Relationships",
    items: [
      "Couples Therapy",
      "Marriage Issues",
      "Divorce & Separation",
      "Communication Problems",
      "Trust Issues",
      "Infidelity",
      "Intimacy Issues",
      "Codependency",
      "Boundary Setting",
      "Family Conflict"
    ]
  },
  {
    category: "Trauma & PTSD",
    items: [
      "Childhood Trauma",
      "Adult Trauma",
      "PTSD",
      "Complex PTSD",
      "Sexual Abuse",
      "Physical Abuse",
      "Emotional Abuse",
      "Neglect",
      "Medical Trauma",
      "Accident/Injury Trauma"
    ]
  },
  {
    category: "Life Transitions",
    items: [
      "Career Changes",
      "Retirement",
      "Moving/Relocation",
      "Pregnancy",
      "New Parents",
      "Empty Nest",
      "Aging",
      "Identity Exploration",
      "Coming Out",
      "Immigration/Cultural Adjustment"
    ]
  }
]

// Therapeutic modalities
export const MODALITIES_CATEGORIZED = [
  {
    category: "Evidence-Based Therapies",
    items: [
      {
        name: "Cognitive Behavioral Therapy (CBT)",
        description: "Focuses on changing thought patterns and behaviors"
      },
      {
        name: "Dialectical Behavior Therapy (DBT)",
        description: "Combines CBT with mindfulness and emotion regulation"
      },
      {
        name: "Acceptance and Commitment Therapy (ACT)",
        description: "Emphasizes psychological flexibility and values"
      },
      {
        name: "Eye Movement Desensitization and Reprocessing (EMDR)",
        description: "Processes traumatic memories through bilateral stimulation"
      }
    ]
  },
  {
    category: "Psychodynamic Approaches",
    items: [
      {
        name: "Psychodynamic Therapy",
        description: "Explores unconscious patterns and past experiences"
      },
      {
        name: "Psychoanalytic Therapy",
        description: "In-depth exploration of unconscious mind"
      },
      {
        name: "Attachment-Based Therapy",
        description: "Focuses on early attachment patterns"
      }
    ]
  },
  {
    category: "Humanistic & Experiential",
    items: [
      {
        name: "Person-Centered Therapy",
        description: "Emphasizes self-discovery and personal growth"
      },
      {
        name: "Gestalt Therapy",
        description: "Focuses on present awareness and personal responsibility"
      },
      {
        name: "Existential Therapy",
        description: "Explores meaning, freedom, and authenticity"
      },
      {
        name: "Emotion-Focused Therapy (EFT)",
        description: "Works with emotions to create change"
      }
    ]
  },
  {
    category: "Somatic & Body-Based",
    items: [
      {
        name: "Somatic Experiencing",
        description: "Releases trauma stored in the body"
      },
      {
        name: "Sensorimotor Psychotherapy",
        description: "Integrates body awareness with talk therapy"
      },
      {
        name: "Body-Mind Centering",
        description: "Explores mind-body connections"
      }
    ]
  },
  {
    category: "Systems & Relational",
    items: [
      {
        name: "Family Systems Therapy",
        description: "Views problems within family context"
      },
      {
        name: "Emotionally Focused Therapy (EFT) for Couples",
        description: "Strengthens emotional bonds in relationships"
      },
      {
        name: "Gottman Method",
        description: "Research-based approach to couples therapy"
      },
      {
        name: "Imago Relationship Therapy",
        description: "Helps couples understand relationship patterns"
      }
    ]
  },
  {
    category: "Integrative & Other Approaches",
    items: [
      {
        name: "Integrative Therapy",
        description: "Combines multiple therapeutic approaches"
      },
      {
        name: "Mindfulness-Based Therapy",
        description: "Incorporates meditation and present-moment awareness"
      },
      {
        name: "Solution-Focused Brief Therapy",
        description: "Focuses on solutions rather than problems"
      },
      {
        name: "Narrative Therapy",
        description: "Helps rewrite personal stories"
      },
      {
        name: "Internal Family Systems (IFS)",
        description: "Works with different parts of self"
      },
      {
        name: "Art Therapy",
        description: "Uses creative expression for healing"
      },
      {
        name: "Play Therapy",
        description: "Uses play for child therapy"
      }
    ]
  }
]

// Flat list of modalities for dropdowns
export const MODALITIES = MODALITIES_CATEGORIZED.flatMap(category => 
  category.items.map(item => ({
    value: item.name,
    label: item.name
  }))
)

// Session-related constants
export const SESSION_DURATION = {
  STANDARD: 50,
  EXTENDED: 80,
  BRIEF: 30,
  INTAKE: 90
} as const

export const SESSION_TYPES = [
  { value: 'individual', label: 'Individual Therapy' },
  { value: 'couples', label: 'Couples Therapy' },
  { value: 'family', label: 'Family Therapy' },
  { value: 'group', label: 'Group Therapy' }
] as const

// Area of focus category mapping for client app
export const AREA_OF_FOCUS_CATEGORIES: Record<string, string[]> = AREAS_OF_FOCUS.reduce((acc, category) => {
  acc[category.category] = category.items;
  return acc;
}, {} as Record<string, string[]>);

// Alternative flat structure for areas of focus (for backward compatibility)
export const AREAS_OF_FOCUS_FLAT: Record<string, string[]> = {
  "Anxiety & Stress": [
    "Generalized Anxiety",
    "Social Anxiety",
    "Panic Attacks",
    "Work-Related Stress",
    "Burnout & Overwhelm",
    "High-Functioning Anxiety"
  ],
  "Depression & Emotional Numbness": [
    "Chronic Low Mood",
    "Lack of Motivation or Joy",
    "Emotional Flatness or Disconnection",
    "Existential Depression"
  ],
  "Relationships & Attachment": [
    "Relationship Conflict & Communication",
    "Dating & Romantic Patterns",
    "Codependency & Boundary Challenges",
    "Attachment Styles",
    "Trust, Betrayal & Repair",
    "Friendships & Family Dynamics"
  ],
  "Self-Esteem & Confidence": [
    "People-Pleasing & Perfectionism",
    "Shame & Inner Critic",
    "Impostor Syndrome",
    "Body Image & Self-Acceptance",
    "Learning to Take Up Space"
  ],
  "Life Transitions & Identity": [
    "Quarter-Life & Midlife Crises",
    "Career Changes",
    "Breakups & Divorce",
    "Moving & Cultural Adjustment",
    "Parenthood & Role Shifts"
  ],
  "Trauma & Complex Histories": [
    "Childhood Trauma",
    "Emotional Neglect",
    "Complex PTSD",
    "Intergenerational Trauma",
    "Dissociation & Numbness"
  ],
  "Women's Mental Health": [
    "People-Pleasing & Boundaries",
    "Motherhood & Identity",
    "Body Image & Aging",
    "Gender-Based Trauma",
    "Balancing Independence & Relationships"
  ],
  "Men's Mental Health": [
    "Emotional Expression",
    "Anger & Healthy Masculinity",
    "Fatherhood & Role Expectations",
    "Work Pressure & Provider Stress",
    "Male Loneliness & Connection"
  ],
  "LGBTQ+ & Gender Identity": [
    "Coming Out & Self-Acceptance",
    "Gender Dysphoria & Transition",
    "Queer Relationship Dynamics",
    "Family Rejection & Chosen Family",
    "Internalized Homophobia/Transphobia"
  ],
  "Neurodivergence & ADHD/Autism": [
    "ADHD Diagnosis & Management",
    "Autism & Social Navigation",
    "Executive Dysfunction",
    "Sensory Sensitivities",
    "Masking & Burnout"
  ],
  "Addiction & Compulsive Behaviors": [
    "Substance Use & Harm Reduction",
    "Behavioral Addictions",
    "Codependency & Enabling",
    "Recovery & Relapse",
    "Family Impact of Addiction"
  ],
  "Chronic Illness & Health": [
    "Living with Chronic Pain",
    "Medical Trauma",
    "Health Anxiety",
    "Grief Over Lost Abilities",
    "Caregiver Stress"
  ],
  "Work & Career": [
    "Workplace Conflict",
    "Career Indecision",
    "Leadership Challenges",
    "Work-Life Integration",
    "Entrepreneurial Stress"
  ],
  "Financial Stress": [
    "Financial Anxiety",
    "Overspending & Emotional Spending",
    "Wealth Guilt",
    "Financial Trauma",
    "Career Transitions"
  ],
  "Existential & Spiritual": [
    "Life Purpose & Meaning",
    "Death Anxiety",
    "Spiritual Crisis",
    "Religious Trauma",
    "Existential Dread"
  ],
  "Grief & Loss": [
    "Death of Loved Ones",
    "Ambiguous Loss",
    "Pet Loss",
    "Loss of Identity",
    "Collective Grief"
  ],
  "Anger & Emotional Regulation": [
    "Chronic Irritability",
    "Passive-Aggression",
    "Revenge Fantasies",
    "Conflict Patterns",
    "Anger as Secondary Emotion"
  ],
  "Pregnancy & Perinatal Mental Health": [
    "Pregnancy Anxiety",
    "Postpartum Depression",
    "Birth Trauma",
    "Fertility Struggles",
    "Pregnancy Loss"
  ]
}

// Modality descriptions for client app
export const MODALITY_DESCRIPTIONS: Record<string, string> = {
  // Cognitive & Behavioral Approaches
  "CBT (Cognitive Behavioral Therapy)": "CBT helps you identify and change negative thought patterns that contribute to emotional distress. You'll learn practical strategies to challenge unhelpful thinking and develop healthier behaviors. Sessions focus on present-day problems and teach you skills you can use immediately. This approach is particularly effective for anxiety, depression, and stress management.",
  
  "DBT (Dialectical Behavior Therapy)": "DBT teaches you skills to manage intense emotions and improve relationships through four key areas: mindfulness, distress tolerance, emotion regulation, and interpersonal effectiveness. You'll learn to balance accepting yourself while working toward positive change. This approach is especially helpful for emotional intensity, self-harm behaviors, and relationship difficulties.",
  
  "ACT (Acceptance & Commitment)": "ACT helps you accept difficult thoughts and feelings while taking action aligned with your values. Rather than fighting against unwanted experiences, you'll learn mindfulness skills to observe them without being controlled by them. This approach emphasizes psychological flexibility and is particularly helpful for anxiety, chronic pain, and feeling stuck in life patterns.",
  
  "CPT (Cognitive Processing Therapy)": "CPT is specifically designed to help you process traumatic experiences by examining how trauma has affected your thoughts about yourself, others, and the world. You'll work through stuck points—thoughts that keep you from recovering—and develop a more balanced perspective. This structured approach typically takes 12 sessions and is highly effective for PTSD.",
  
  "MI (Motivational Interviewing)": "MI helps you explore and resolve ambivalence about change in a supportive, non-judgmental way. Your therapist will help you identify your own motivations for change rather than telling you what to do. This collaborative approach is particularly effective for substance use, health behaviors, and any situation where you feel stuck between wanting and not wanting to change.",
  
  "Solution-Focused Therapy": "This brief therapy approach focuses on finding solutions rather than analyzing problems. You'll identify what's already working in your life and build on those strengths to create positive change. Sessions emphasize your existing resources and abilities, making it ideal when you want practical results quickly.",
  
  // Trauma & Somatic Approaches
  "EMDR (Eye Movement Desensitization)": "EMDR helps your brain reprocess traumatic memories using bilateral stimulation (eye movements, taps, or sounds) while recalling difficult experiences. This allows stuck memories to integrate properly, reducing their emotional charge. Many people experience significant relief from trauma symptoms in fewer sessions than traditional talk therapy.",
  
  "Somatic Therapy": "Somatic therapy recognizes that trauma and stress are stored in your body, not just your mind. You'll learn to tune into physical sensations and release trapped tension through breathwork, movement, or mindful awareness. This body-based approach is especially helpful for trauma, chronic stress, and feeling disconnected from yourself.",
  
  "SE (Somatic Experiencing)": "SE focuses on releasing trauma stored in your nervous system by gently guiding you to notice and discharge trapped survival energy. You'll work with subtle body sensations to complete interrupted defensive responses from past traumatic events. This approach helps restore your nervous system's natural resilience without requiring you to relive traumatic memories.",
  
  "Sensorimotor Psychotherapy": "This approach integrates talk therapy with body awareness to address trauma's impact on both mind and body. You'll learn to track physical sensations, movements, and postures that hold traumatic memories. By working with these body-based experiences alongside thoughts and emotions, you can achieve more complete healing.",
  
  "Brainspotting": "Brainspotting uses specific eye positions to access and process trauma stored in the deeper parts of your brain. Your therapist will help you find 'brainspots'—eye positions that connect to traumatic memories—allowing for focused processing. This approach can access trauma that may be difficult to reach through talking alone.",
  
  "Trauma-Informed Therapy": "This approach prioritizes your safety, choice, and empowerment throughout the therapeutic process. Your therapist understands how trauma affects the brain and body, and adapts their approach to avoid re-traumatization. Every aspect of therapy is designed to help you feel safe and in control of your healing journey.",
  
  // Couples & Family Therapy
  "EFT (Emotionally Focused Therapy)": "EFT helps couples identify negative patterns and create secure emotional bonds. You'll learn to recognize the cycle that keeps you stuck and develop new ways of connecting. This approach is based on attachment science and has strong research support for improving relationship satisfaction.",
  
  "Gottman Method": "Based on 40+ years of research, the Gottman Method helps couples build stronger relationships through practical tools and exercises. You'll learn to manage conflict constructively, deepen friendship and intimacy, and create shared meaning. This structured approach includes assessments to identify your relationship's specific strengths and challenges.",
  
  "Imago Relationship Therapy": "Imago helps you understand how childhood experiences influence your choice of partner and relationship patterns. You'll learn structured dialogue techniques to communicate more effectively and heal old wounds together. This approach views conflict as an opportunity for growth and deeper connection.",
  
  "RLT (Relational Life Therapy)": "RLT addresses the real issues that couples face with direct, practical interventions. Your therapist will actively coach you on developing intimacy skills and breaking destructive patterns. This approach emphasizes personal responsibility and teaches concrete tools for creating a more passionate, authentic relationship.",
  
  "AEDP for Couples": "This approach helps couples heal attachment wounds and deepen emotional connection through transformative experiences in session. You'll work with core emotions in a safe, supportive environment to create lasting change. The focus is on building positive cycles of connection and co-regulation.",
  
  "Discernment Counseling": "Designed for couples where one partner is considering divorce while the other wants to save the relationship. This brief approach (1-5 sessions) helps you gain clarity and confidence about your path forward. The goal is to help you make a decision about your relationship that you can feel good about.",
  
  "Integrative Behavioral Couple Therapy": "IBCT combines behavior change strategies with acceptance work to help couples. You'll learn to accept differences that can't be changed while working together on problems that can be solved. This balanced approach helps create lasting improvements in relationship satisfaction.",
  
  "Restoration Therapy": "This approach helps couples identify and heal the pain cycle that keeps them stuck. You'll explore how feelings of identity and safety drive destructive patterns and learn to respond differently. The focus is on restoring love and peace to your relationship.",
  
  "Structural Family Therapy": "This approach views problems as arising from dysfunctional family structures and boundaries. Your therapist will help reorganize family relationships to create healthier patterns. Sessions may involve different family members at different times to address specific relationship dynamics.",
  
  "Strategic Family Therapy": "Strategic therapy focuses on solving specific problems through targeted interventions. Your therapist will design strategies to interrupt problem patterns and create positive change. This brief, focused approach is particularly effective for families dealing with adolescent behavior problems.",
  
  "Bowenian Family Therapy": "Based on family systems theory, this approach helps you understand how patterns are transmitted across generations. You'll work on differentiating yourself while maintaining connections with family members. The focus is on managing anxiety and reactivity within the family system.",
  
  // Psychodynamic & Depth Psychology
  "Psychodynamic Therapy": "This approach explores how unconscious thoughts and past experiences influence your current life. You'll examine relationship patterns, defense mechanisms, and inner conflicts to gain deeper self-understanding. The therapeutic relationship itself becomes a tool for understanding and changing problematic patterns.",
  
  "Relational Psychodynamic": "This couples-focused approach explores how each partner's unconscious patterns interact to create relationship dynamics. You'll examine how past relationships influence your current partnership. The therapy relationship provides a safe space to understand and transform these deep patterns.",
  
  "Schema Therapy": "Schema therapy helps you identify and change deep-rooted patterns (schemas) developed in childhood. You'll work with different 'modes' or parts of yourself to heal emotional wounds and develop healthier coping strategies. This integrative approach combines cognitive, behavioral, and experiential techniques.",
  
  "IFS (Internal Family Systems)": "IFS helps you understand and harmonize the different 'parts' of your personality. You'll learn to access your core Self—a calm, compassionate center—and help wounded parts heal. This approach is particularly powerful for trauma, internal conflict, and self-criticism.",
  
  "Voice Dialogue": "This approach helps you explore and integrate different 'selves' or sub-personalities within you. You'll learn to recognize when different parts take over and develop a more conscious relationship with them. The goal is to develop an aware ego that can choose which self to engage in different situations.",
  
  "ISTDP (Intensive Short-Term)": "ISTDP rapidly accesses and processes unconscious emotions that drive symptoms. Your therapist will actively help you overcome defenses and experience core feelings. This intensive approach can create significant change in a shorter time frame than traditional therapy.",
  
  // Humanistic & Experiential
  "Person-Centered Therapy": "This approach trusts in your innate capacity for growth and self-direction. Your therapist provides unconditional positive regard, empathy, and genuineness, creating a safe space for self-exploration. You lead the process, exploring whatever feels most important to you.",
  
  "Gestalt Therapy": "Gestalt therapy focuses on immediate awareness and personal responsibility. You'll use experiential techniques like the 'empty chair' to explore unfinished business and integrate different aspects of yourself. The emphasis is on what's happening in the here-and-now of your experience.",
  
  "Existential Therapy": "This approach explores fundamental questions about meaning, freedom, death, and isolation. You'll examine how you create meaning in your life and take responsibility for your choices. It's particularly helpful during major life transitions or when facing existential concerns.",
  
  "Narrative Therapy": "Narrative therapy helps you separate yourself from your problems and rewrite your life story. You'll explore how cultural and family narratives have shaped your identity and work to reclaim your preferred story. This empowering approach is especially helpful for those feeling defined by their problems.",
  
  "Pragmatic/Experiential Therapy": "This couples approach focuses on creating positive experiences in the here-and-now of therapy sessions. You'll engage in structured exercises designed to deepen intimacy and connection. The emphasis is on experiencing change rather than just talking about it.",
  
  "CFT (Compassion-Focused Therapy)": "CFT helps you develop self-compassion as an antidote to shame and self-criticism. You'll learn practices from neuroscience and Buddhist psychology to activate your soothing system. This approach is particularly helpful for those with high shame and self-criticism.",
  
  // Mindfulness & Integrative
  "Mindfulness-Based Therapy": "These approaches integrate meditation and mindfulness practices to help you develop a different relationship with difficult thoughts and feelings. You'll learn to observe experiences without judgment and respond more skillfully to challenges. Regular practice helps reduce stress and prevent relapse of depression or anxiety.",
  
  "Integrative Therapy": "Your therapist draws from multiple therapeutic approaches to create a treatment tailored to your unique needs. They might combine techniques from different modalities depending on what's most helpful for your specific concerns. This flexible approach allows therapy to adapt as your needs change.",
  
  "AEDP (Accelerated Experiential)": "AEDP helps you process difficult emotions in the safety of a caring therapeutic relationship. You'll work with core emotions to create transformative experiences that lead to healing. The focus is on undoing aloneness and fostering resilience through connection.",
  
  "Attachment-Based Therapy": "This approach explores how early relationships created templates for how you connect with others. You'll examine your attachment style and work to develop more secure relationship patterns. The therapeutic relationship provides a corrective experience for healing attachment wounds.",
  
  "Contextual Therapy": "This family therapy approach emphasizes fairness and ethical relationships between family members. You'll explore invisible loyalties and work toward more balanced give-and-take in relationships. The focus is on healing through addressing relational ethics and justice.",
  
  "IPT (Interpersonal Therapy)": "IPT is a structured, time-limited therapy focusing on improving relationships and social functioning. You'll work on one of four problem areas: grief, role disputes, role transitions, or interpersonal deficits. This approach helps relieve symptoms by improving your interpersonal life.",
  
  // Specialized Approaches
  "Art/Expressive Therapy": "These approaches use creative expression—art, music, movement, or drama—as the primary mode of communication and healing. You don't need artistic skill; the process of creation itself is therapeutic. This can be especially helpful when words aren't enough to express your experiences.",
  
  "Sandplay Therapy": "Sandplay provides a nonverbal way to explore your inner world using miniature figures in sand. The sand tray becomes a container for your psyche to express itself symbolically. This approach can access deep healing through the creative unconscious.",
  
  "Hypnotherapy": "Clinical hypnotherapy uses guided relaxation and focused attention to achieve a heightened state of awareness. In this state, you can explore thoughts, feelings, and memories that might be hidden from your conscious mind. This approach can be helpful for anxiety, habits, and accessing inner resources.",
  
  "Ecotherapy": "Ecotherapy integrates nature into the healing process, recognizing the therapeutic benefits of connecting with the natural world. Sessions might take place outdoors or incorporate nature-based practices. This approach is particularly helpful for depression, anxiety, and feeling disconnected from life.",
  
  "FAP (Functional Analytic)": "FAP uses the therapeutic relationship as a live laboratory for change. Your therapist will notice and respond to your interpersonal behaviors as they happen in session. This creates opportunities for immediate practice of new, more effective ways of relating."
}

// Timezone display names for client app
export const TIMEZONE_DISPLAY_NAMES: Record<string, string> = {
  'America/Toronto': 'Toronto Time (EST/EDT)',
  'America/New_York': 'Toronto Time (EST/EDT)',
  'America/Montreal': 'Toronto Time (EST/EDT)',
  'America/Chicago': 'Winnipeg Time (CST/CDT)',
  'America/Winnipeg': 'Winnipeg Time (CT)',
  'America/Denver': 'Calgary Time (MST/MDT)',
  'America/Edmonton': 'Calgary Time (MT)',
  'America/Calgary': 'Calgary Time (MT)',
  'America/Los_Angeles': 'Vancouver Time (PST/PDT)',
  'America/Vancouver': 'Vancouver Time (PT)',
  'America/Halifax': 'Halifax Time (AST/ADT)',
  'America/St_Johns': 'St. John\'s Time (NST/NDT)',
  'America/Regina': 'Regina Time (CST)',
  'EST': 'Toronto Time (EST/EDT)',
  'CST': 'Winnipeg Time (CST/CDT)',
  'MST': 'Calgary Time (MST/MDT)',
  'PST': 'Vancouver Time (PST/PDT)',
  'AST': 'Halifax Time (AST/ADT)',
  'NST': 'St. John\'s Time (NST/NDT)'
}

/**
 * Human labels for every TherapyType enum value in the DB.
 *
 * Add a new entry here whenever a new value is added to the enum
 * (schema.prisma -> enum TherapyType). Call sites that render the
 * raw enum string to users should go through `getTherapyTypeLabel()`
 * so clients + staff see "Quieting Anxiety" instead of "PIF_ANXIETY".
 */
export const THERAPY_TYPE_LABELS: Record<string, string> = {
  INDIVIDUAL: 'Individual',
  COUPLES: 'Couples',
  FAMILY: 'Family',
  GROUP: 'Group',
  // Self-guided iCBT programs (imwell app). The "iCBT" tag matters —
  // staff need to tell these apart from therapist-led episodes at a
  // glance. Add a new line for each new iCBT pack.
  ICBT_UNSPIRAL: 'Unspiral (iCBT · anxiety)',
  ICBT_UPLIFT: 'Uplift (iCBT · depression)',
  // Pay-It-Forward programs (client app). Prefixed with "Pay It
  // Forward:" so staff can tell them apart from standard individual
  // therapy at a glance.
  PIF_ANXIETY: 'Pay It Forward: Quieting Anxiety',
  PIF_BREAKUP: 'Pay It Forward: Healing After a Breakup',
  PIF_BOUNDARIES: 'Pay It Forward: Healthy Boundaries',
  PIF_UNSTUCK: 'Pay It Forward: Getting Unstuck',
}

/**
 * Convert a TherapyType enum value (any casing) to a human label.
 * Falls back to the raw value when unknown so we don't silently drop
 * a new program that forgot to update THERAPY_TYPE_LABELS.
 */
export function getTherapyTypeLabel(therapyType: string | null | undefined): string {
  if (!therapyType) return ''
  const upper = therapyType.toUpperCase()
  return THERAPY_TYPE_LABELS[upper] ?? therapyType
}

/**
 * Program structure for Pay-It-Forward: every PIF program is 6
 * sessions at the sliding-scale rate the client picked at enrollment.
 * Used by therapist-facing counters ("Session N of 6") on the clients
 * page card + ClientFileSlider so therapists can see where a client
 * is in the program arc. Not yet enforced as a cap in code — the 7th+
 * session books as a regular paid session today.
 */
export const PIF_SESSIONS_PER_PROGRAM = 6

/** True when the TherapyType enum value represents a PIF program. */
export function isPifTherapyType(therapyType: string | null | undefined): boolean {
  return !!therapyType && therapyType.toUpperCase().startsWith('PIF_')
}